// Edit a department's profile: display name, head, hidden.
// Admin / superadmin only (server: CASL `update department`). The stored
// key (departmentName) never changes — it is also the chat topic and
// storage folder — so only the shown name is editable.
import { useEffect, useState } from "react";
import { AlertCircle, Building2, Loader2, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { TeacherCombobox, type TeacherOption } from "../ClassRooms/TeacherCombobox";
import { useInstituteLabels } from "@/lib/instituteLabels";
import { type DepartmentProfile, updateDepartment, withLabels } from "./structureApi";

type Props = {
    department: DepartmentProfile | null;   // null = closed
    onClose: () => void;
    onSaved: (updated: DepartmentProfile) => void;
};

type HeadChoice = { eid: string; name: string } | null;

export function DepartmentEditDialog({ department, onClose, onSaved }: Props) {
    const L = useInstituteLabels();
    const [ displayName, setDisplayName ] = useState("");
    const [ head, setHead ] = useState<HeadChoice>(null);
    const [ hidden, setHidden ] = useState(false);
    const [ saving, setSaving ] = useState(false);
    const [ error, setError ] = useState<string | null>(null);

    useEffect(() => {
        if (!department) return;
        setDisplayName(department.displayName);
        setHead(department.head?.eid ? { eid: department.head.eid, name: department.head.name || department.head.eid } : null);
        setHidden(department.hidden);
        setError(null);
    }, [ department ]);

    if (!department) return null;

    const handleSave = async () => {
        // Send only what changed — the server leaves the rest alone.
        const body: Parameters<typeof updateDepartment>[ 0 ] = { departmentName: department.departmentName };
        if (displayName.trim() !== department.displayName) body.displayName = displayName.trim();
        if ((head?.eid ?? null) !== (department.head?.eid ?? null)) body.headEid = head?.eid ?? null;
        if (hidden !== department.hidden) body.hidden = hidden;
        if (Object.keys(body).length === 1) { onClose(); return; }

        setSaving(true);
        setError(null);
        try {
            onSaved(await withLabels(L, () => updateDepartment(body)));
        } catch (err: any) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Building2 className="h-5 w-5 text-primary" /> Edit {L.department}
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    {error && (
                        <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                            <AlertCircle className="h-4 w-4 shrink-0" /> {error}
                        </div>
                    )}

                    <div className="space-y-1.5">
                        <Label>Name</Label>
                        <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={100} />
                        <p className="text-[11px] text-muted-foreground">
                            Stored as <code>{department.departmentName}</code> (that never changes).
                        </p>
                    </div>

                    <div className="space-y-1.5">
                        <Label>Head of {L.department.toLowerCase()}</Label>
                        <div className="flex items-center gap-2">
                            <div className="flex-1 min-w-0">
                                <TeacherCombobox
                                    value={head ? `${head.name} (${head.eid})` : ""}
                                    placeholder="Search faculty…"
                                    onSelect={(t: TeacherOption) => setHead({ eid: t.employee_id, name: t.name })}
                                />
                            </div>
                            {head && (
                                <Button variant="ghost" size="icon" onClick={() => setHead(null)} title="Remove head">
                                    <X className="h-4 w-4" />
                                </Button>
                            )}
                        </div>
                    </div>

                    <div className="flex items-start justify-between gap-4 rounded-lg border p-3">
                        <div>
                            <Label>Hidden</Label>
                            <p className="text-[11px] text-muted-foreground mt-0.5">
                                For a small institute that runs everything under one {L.department.toLowerCase()}:
                                the apps skip the {L.department.toLowerCase()} step.
                            </p>
                        </div>
                        <Switch checked={hidden} onCheckedChange={setHidden} />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button onClick={handleSave} disabled={saving || !displayName.trim()}>
                        {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Saving…</> : "Save"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
